import { useState } from 'react';
import Sidebar from '../components/Sidebar';

function Users() {
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [showInviteModal, setShowInviteModal] = useState(false);

  const users = [
    {
      id: 1,
      name: 'Sarah Chen',
      email: 'sarah@techflow.com',
      role: 'admin',
      status: 'active',
      lastActive: '2 hours ago',
      joinedDate: '2024-01-01'
    },
    {
      id: 2,
      name: 'Marcus Rodriguez',
      email: 'marcus@datascale.com',
      role: 'user',
      status: 'active',
      lastActive: '1 day ago',
      joinedDate: '2024-01-05'
    },
    {
      id: 3,
      name: 'Emily Thompson',
      email: 'emily@securebase.io',
      role: 'admin',
      status: 'active',
      lastActive: '30 minutes ago',
      joinedDate: '2024-01-03'
    },
    {
      id: 4,
      name: 'David Kim',
      email: 'david@startup.xyz',
      role: 'viewer',
      status: 'active',
      lastActive: '3 days ago',
      joinedDate: '2024-01-10'
    },
    {
      id: 5,
      name: 'Lisa Wang',
      email: 'lisa@company.com',
      role: 'user',
      status: 'inactive',
      lastActive: '2 weeks ago',
      joinedDate: '2023-12-15'
    },
    {
      id: 6,
      name: 'James Miller',
      email: 'james@enterprise.com',
      role: 'viewer',
      status: 'active',
      lastActive: '5 hours ago',
      joinedDate: '2024-01-12'
    },
  ];

  const filteredUsers = users.filter(user => {
    return roleFilter === 'all' || user.role === roleFilter;
  });

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'badge badge-primary';
      case 'user':
        return 'badge bg-blue-500/20 text-blue-400 border border-blue-500';
      case 'viewer':
        return 'badge bg-gray-500/20 text-gray-400 border border-gray-500';
      default:
        return 'badge';
    }
  };

  const getRoleLabel = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <div className="min-h-screen bg-[var(--color-bg-primary)]">
      <Sidebar />

      <div className="ml-64 p-8">
        {/* Header */}
        <header className="mb-8 fade-in-up">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-display mb-2" style={{ color: 'var(--color-text-primary)' }}>
                Users
              </h1>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Manage user access and permissions
              </p>
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="btn-primary"
            >
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Invite User
              </span>
            </button>
          </div>
        </header>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="card">
            <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>Total Users</p>
            <p className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>{users.length}</p>
          </div>
          <div className="card">
            <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>Active</p>
            <p className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {users.filter(u => u.status === 'active').length}
            </p>
          </div>
          <div className="card">
            <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>Admins</p>
            <p className="text-3xl font-bold" style={{ color: 'var(--color-primary)' }}>
              {users.filter(u => u.role === 'admin').length}
            </p>
          </div>
          <div className="card">
            <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>Inactive</p>
            <p className="text-3xl font-bold" style={{ color: 'var(--color-text-secondary)' }}>
              {users.filter(u => u.status === 'inactive').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="card mb-6 fade-in-up delay-100">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Filter by role:</span>
            <div className="flex items-center space-x-2">
              {['all', 'admin', 'user', 'viewer'].map(role => (
                <button
                  key={role}
                  onClick={() => setRoleFilter(role)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    roleFilter === role ? 'text-white' : ''
                  }`}
                  style={
                    roleFilter === role
                      ? { backgroundColor: 'var(--color-primary)' }
                      : { backgroundColor: 'transparent', color: 'var(--color-text-secondary)' }
                  }
                >
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="card fade-in-up delay-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>User</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Email</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Role</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Status</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Last Active</th>
                  <th className="text-right py-4 px-4 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b transition-colors hover:bg-[var(--color-bg-elevated)]" style={{ borderColor: 'var(--color-border)' }}>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: 'var(--color-primary)' }}>
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{user.name}</p>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Joined {user.joinedDate}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{user.email}</td>
                    <td className="py-4 px-4">
                      <span className={`${getRoleBadgeClass(user.role)} text-xs`}>
                        {getRoleLabel(user.role)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <div
                          className={`w-2 h-2 rounded-full ${user.status === 'active' ? 'pulse-dot' : ''}`}
                          style={{ backgroundColor: user.status === 'active' ? '#10B981' : '#9CA3AF' }}
                        />
                        <span className="text-sm capitalize" style={{ color: 'var(--color-text-secondary)' }}>
                          {user.status}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>{user.lastActive}</td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        <button className="p-2 rounded hover:bg-[var(--color-bg-elevated)] transition-colors" title="Edit user">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ color: 'var(--color-text-secondary)' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                        <button className="p-2 rounded hover:bg-[var(--color-bg-elevated)] transition-colors" title="Send invite">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ color: 'var(--color-text-secondary)' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                        <button
                          className="p-2 rounded hover:bg-yellow-900/20 transition-colors"
                          title={user.status === 'active' ? 'Deactivate user' : 'Activate user'}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{ color: '#F59E0B' }}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                            />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <p className="text-lg font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>No users found</p>
              <p style={{ color: 'var(--color-text-secondary)' }}>Try adjusting your filters</p>
            </div>
          )}
        </div>

        {/* Invite Modal (Simple placeholder) */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowInviteModal(false)}>
            <div className="card max-w-md w-full m-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Invite User</h2>
                <button onClick={() => setShowInviteModal(false)} className="p-2 rounded hover:bg-[var(--color-bg-elevated)]">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-text-secondary)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="user@company.com"
                    className="w-full px-4 py-3 border rounded-lg"
                    style={{
                      backgroundColor: 'var(--color-bg-light)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
                    Role
                  </label>
                  <select
                    className="w-full px-4 py-3 border rounded-lg"
                    style={{
                      backgroundColor: 'var(--color-bg-light)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                <div className="flex items-center space-x-3 pt-4">
                  <button onClick={() => setShowInviteModal(false)} className="btn-secondary flex-1">
                    Cancel
                  </button>
                  <button className="btn-primary flex-1">
                    Send Invite
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Users;
